import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { exportCSV, exportJSON, grade, gradeColor, importJSON, loadDB, paidTotal, tuitionPaid, uid } from '../db';
import {
  Toast, toast, Badge, Btn, Card, CardHead, Modal, ModalFooter,
  Field, Input, Select, Grid, Span, SecLabel, Search, Avatar, Stat,
  Tabs, PhotoZone, NoData, TblWrap,
} from '../ui.jsx';
import {
  buildCard, buildCardBack, printCard, printClassCards, printTC,
  printCC, buildTC, buildCC, printBonafide, buildBonafide,
} from '../print.js';
import { adminUploadStudentDocument, getSessions, getStudentDocument, getStudentDocuments, getStudents, initShadow, loadSessionData, reviewStudentDocument } from '../storage';
import { fmtDob } from '../utils/fmtDob';

const DOC_TYPES_ADMIN = [
  { id: 'aadhar_student', label: "Student's Aadhaar Card (Both Sides)" },
  { id: 'aadhar_father', label: "Father's Aadhaar Card (Both Sides)" },
  { id: 'aadhar_mother', label: "Mother's Aadhaar Card (Both Sides)" },
  { id: 'photograph', label: 'Passport Photograph' },
  { id: 'birth_cert', label: 'Birth Certificate' },
  { id: 'family_photo', label: 'Group Photograph with Parents' },
  { id: 'tc', label: 'Transfer Certificate (Previous School)' },
  { id: 'char_cert', label: 'Character Certificate (Previous School)' },
  { id: 'prev_rc', label: 'Report Card of Previous Class' },
];

function AdminStudentDocs({ activeSessionId, sid }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState({});
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState({});
  const [uploading, setUploading] = useState({});

  const load = () => {
    if (!activeSessionId || !sid) { setLoading(false); return; }
    getStudentDocuments(activeSessionId, sid)
      .then(r => setDocs(r.documents || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [activeSessionId, sid]);

  const review = async (docId, status, reason = '') => {
    setBusy(b => ({ ...b, [docId]: true }));
    try {
      await reviewStudentDocument(activeSessionId, sid, docId, status, reason);
      setRejecting(null); setRejectReason('');
      await load();
    } catch (e) { toast(e.message || 'Failed', 'err'); }
    finally { setBusy(b => ({ ...b, [docId]: false })); }
  };

  const handleAdminUpload = (type, label) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast('File too large. Max 10MB.', 'err'); return; }
      setUploading(u => ({ ...u, [type]: true }));
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          await adminUploadStudentDocument(activeSessionId, sid, { type, label, data: ev.target.result, mimeType: file.type });
          toast('Document uploaded & approved');
          await load();
        } catch (e) { toast(e.message || 'Upload failed', 'err'); }
        finally { setUploading(u => ({ ...u, [type]: false })); }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const statusColor = { approved: '#059669', rejected: '#dc2626', pending: '#b45309' };
  const statusBg = { approved: '#dcfce7', rejected: '#fef2f2', pending: '#fef9c3' };

  if (!activeSessionId || !sid) return null;

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 15, color: '#1960a3', fontVariationSettings: "'FILL' 1" }}>folder_open</span>
        Student Documents
        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', marginLeft: 4 }}>Admin uploads are auto-approved</span>
      </div>
      {loading ? <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>Loading…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DOC_TYPES_ADMIN.map(dt => {
            const doc = docs.find(d => d.type === dt.id);
            const st = doc?.status || null;
            const upBusy = uploading[dt.id];
            return (
              <div key={dt.id} style={{ padding: '10px 12px', borderRadius: 8, background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{dt.label}</span>
                  {st && <span style={{ fontSize: 10, fontWeight: 700, color: statusColor[st], background: statusBg[st], padding: '2px 8px', borderRadius: 20 }}>{st.charAt(0).toUpperCase() + st.slice(1)}</span>}
                  {!st && <span style={{ fontSize: 10, fontWeight: 600, color: '#cbd5e1' }}>Not uploaded</span>}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {doc && (
                      <button onClick={async () => {
                        setPreviewing(p => ({ ...p, [doc.id]: true }));
                        try { const r = await getStudentDocument(activeSessionId, sid, doc.id); setPreview({ label: dt.label, data: r.doc.data, mimeType: r.doc.mimeType }); }
                        catch (e) { toast('Failed to load', 'err'); }
                        finally { setPreviewing(p => ({ ...p, [doc.id]: false })); }
                      }} style={{ fontSize: 11, fontWeight: 700, color: '#1960a3', background: '#eef4ff', border: '1px solid #c7d9f5', borderRadius: 6, padding: '4px 9px', cursor: 'pointer' }}>
                        {previewing[doc.id] ? '…' : 'View'}
                      </button>
                    )}
                    {/* Admin upload / re-upload */}
                    <button onClick={() => handleAdminUpload(dt.id, dt.label)} disabled={upBusy}
                      style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: doc ? 'linear-gradient(135deg,#7c3aed,#6d28d9)' : 'linear-gradient(135deg,#1960a3,#0d47a1)', border: 'none', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, opacity: upBusy ? 0.7 : 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{upBusy ? 'progress_activity' : 'upload'}</span>
                      {upBusy ? '…' : doc ? 'Replace' : 'Upload'}
                    </button>
                    {doc && st !== 'approved' && (
                      <button onClick={() => review(doc.id, 'approved')} disabled={busy[doc.id]}
                        style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#059669', border: 'none', borderRadius: 6, padding: '4px 9px', cursor: 'pointer' }}>✓ Approve</button>
                    )}
                    {doc && st !== 'rejected' && (
                      <button onClick={() => setRejecting(doc.id)}
                        style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 9px', cursor: 'pointer' }}>✗ Reject</button>
                    )}
                  </div>
                </div>
                {doc?.rejectReason && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>Reason: {doc.rejectReason}</div>}
                {doc?.uploadedBy && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Uploaded by: {doc.uploadedBy}</div>}
                {rejecting === doc?.id && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason (optional)"
                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #fecaca', fontSize: 12, outline: 'none' }} />
                    <button onClick={() => review(doc.id, 'rejected', rejectReason)} disabled={busy[doc.id]}
                      style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#dc2626', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>Confirm</button>
                    <button onClick={() => { setRejecting(null); setRejectReason(''); }}
                      style={{ fontSize: 11, fontWeight: 600, color: '#64748b', background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>Cancel</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 16, maxWidth: 560, width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{preview.label}</div>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8' }}>✕</button>
            </div>
            {preview.mimeType?.startsWith('image') ? (
              <img src={preview.data} alt={preview.label} style={{ width: '100%', borderRadius: 8 }} />
            ) : (
              <iframe src={preview.data} style={{ width: '100%', height: 480, border: 'none', borderRadius: 8 }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Students({ db, save, setPage, activeSessionId }) {
  const [refreshing, setRefreshing] = React.useState(false);

  const refreshFromServer = async () => {
    if (!activeSessionId) return;
    setRefreshing(true);
    try {
      const raw = await loadSessionData(activeSessionId);
      if (raw) {
        const defaults = loadDB();
        const merged = { ...defaults, ...raw };
        initShadow(activeSessionId, merged);
        save(merged);
      }
    } catch (e) { toast('Refresh failed', 'err'); }
    finally { setRefreshing(false); }
  };
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = React.useRef(null);
  const [filterCls, setFilterCls] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [editId, setEditId] = useState(null);
  const [view, setView] = useState('summary'); // 'summary' | 'directory'
  const [expandedCls, setExpandedCls] = useState(null);
  const [showPpass, setShowPpass] = useState(false);

  // Import from previous session state
  const [importOpen, setImportOpen] = useState(false);
  const [importSessions, setImportSessions] = useState([]);
  const [importFromId, setImportFromId] = useState('');
  const [importStudents, setImportStudents] = useState([]); // students from prev session
  const [importLoading, setImportLoading] = useState(false);
  const [importSearch, setImportSearch] = useState('');
  const [importFilterCls, setImportFilterCls] = useState('');
  const [importSelected, setImportSelected] = useState({}); // { sid: newCls }
  const [importSaving, setImportSaving] = useState(false);
  const [importStep, setImportStep] = useState('pick'); // 'pick' | 'assign'

  const openImport = async () => {
    setImportStep('pick');
    setImportFromId('');
    setImportStudents([]);
    setImportSelected({});
    setImportSearch('');
    setImportFilterCls('');
    setImportOpen(true);
    try {
      const all = await getSessions();
      // exclude current session
      setImportSessions(all.filter(s => (s.sid || s.id) !== activeSessionId));
    } catch (e) { toast('Failed to load sessions', 'err'); }
  };

  const loadImportStudents = async (sessId) => {
    setImportFromId(sessId);
    setImportLoading(true);
    setImportStudents([]);
    setImportSelected({});
    setImportFilterCls('');
    try {
      const list = await getStudents(sessId);
      setImportStudents(list);
      // Pre-select all, default class = same class
      const sel = {};
      list.forEach(s => { sel[s.sid || s.id] = s.cls || ''; });
      setImportSelected(sel);
      setImportStep('assign');
    } catch (e) { toast('Failed to load students', 'err'); }
    setImportLoading(false);
  };

  const doImport = () => {
    const toImport = importStudents.filter(s => importSelected[s.sid || s.id] !== undefined && importSelected[s.sid || s.id] !== '__skip__');
    if (!toImport.length) { toast('No students selected', 'err'); return; }
    setImportSaving(true);
    const existingIds = new Set(db.students.map(s => s.sid || s.id));
    const newStudents = [];
    toImport.forEach(s => {
      const sid = s.sid || s.id;
      if (existingIds.has(sid)) return; // already in this session
      const newCls = importSelected[sid] || s.cls || '';
      // Strip session-specific fields, assign new class
      const { _idCardGenerated, _idCardGeneratedAt, _idCardTheme, _idCardYear, _idCardPrin, _idCardPhone,
        otp, otpExpiry, pendingEmail, reportCard, sessionId, _id, __v, createdAt, updatedAt, ...rest } = s;
      newStudents.push({ ...rest, id: sid, sid, cls: newCls, fst: 'Pending', ppass: '', puser: '' });
    });
    if (!newStudents.length) { toast('All selected students already exist in this session', 'err'); setImportSaving(false); return; }
    save({ ...db, students: [...db.students, ...newStudents] });
    toast(`Imported ${newStudents.length} student${newStudents.length !== 1 ? 's' : ''}`);
    setImportSaving(false);
    setImportOpen(false);
  };

  const importClasses = [...new Set(importStudents.map(s => s.cls).filter(Boolean))].sort();
  const filteredImport = importStudents.filter(s => {
    const q = importSearch.trim().toLowerCase();
    const matchSearch = !q || `${s.fn || ''} ${s.ln || ''} ${s.cls || ''} ${s.admno || ''}`.toLowerCase().includes(q);
    const matchCls = !importFilterCls || s.cls === importFilterCls;
    return matchSearch && matchCls;
  });
  const selectedCount = Object.values(importSelected).filter(v => v !== '__skip__').length;

  // Default to first class on mount
  useEffect(() => {
    if (!filterCls && db.classes.length > 0) {
      setFilterCls(db.classes[0].name);
    }
  }, []); // eslint-disable-line
  const classes = db.classes.map(c => c.name);

  // close dropdown on outside click
  useEffect(() => {
    const h = e => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // live suggestions from search query
  const suggestions = React.useMemo(() => {
    if (!search.trim()) return [];
    const lq = search.toLowerCase();
    return db.students.filter(s =>
      `${s.fn || ''} ${s.ln || ''}`.toLowerCase().includes(lq) ||
      (s.cls || '').toLowerCase().includes(lq) ||
      (s.admno || '').toLowerCase().includes(lq) ||
      (s.roll || '').toLowerCase().includes(lq) ||
      (s.fphone || s.ph || '').includes(lq) ||
      (s.father || '').toLowerCase().includes(lq)
    ).slice(0, 8);
  }, [search, db.students]);

  const [saving, setSaving] = useState(false);

  const openAdd = () => { setForm({ fn: '', ln: '', cls: '', fst: 'Pending', mf: 0, fextras: [], gn: 'Male' }); setPhoto(null); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...s, sid: s.sid || s.id }); setPhoto(db.photos[s.sid] || db.photos[s.id] || null); setEditId(s.sid || s.id); setOpen(true); };
  const saveStu = async () => {
    if (saving) return;
    if (!form.fn?.trim()) { toast('First name required', 'err'); return; }
    if (!form.cls?.trim()) { toast('Class is required', 'err'); return; }
    setSaving(true);
    const id = editId || 'S' + uid();
    const mf = parseFloat(form.mf) || 0;
    const fextras = form.fextras || [];
    const extrasSum = fextras.reduce((s, e) => s + (parseFloat(e.amt) || 0), 0);
    const fee = mf * 12 + extrasSum;
    const s = { ...form, id, sid: id, father: form.fa || form.father || '', mother: form.ma || form.mother || '', fphone: form.fp || form.fphone || '', blood: form.bl || form.blood || '', roll: form.ro || form.roll || '', admno: form.an || form.admno || '', aadhar: form.aa || form.aadhar || '', city: form.cy || form.city || '', caste: form.ca || form.caste || '', religion: form.re || form.religion || '', fst: form.fst || 'Pending', mf, fextras, fee };
    const newStudents = editId ? db.students.map(x => (x.sid || x.id) === id ? s : x) : [...db.students, s];
    const newPhotos = { ...db.photos };
    if (photo) newPhotos[id] = photo; else if (editId) delete newPhotos[id];
    save({ ...db, students: newStudents, photos: newPhotos });
    // Sync profile photo to passport photograph document
    if (photo && activeSessionId) {
      const sid = s.sid || id;
      try {
        await adminUploadStudentDocument(activeSessionId, sid, {
          type: 'photograph', label: 'Passport Photograph', data: photo, mimeType: 'image/jpeg',
        });
      } catch (e) { } // silent — don't block save
    }
    setOpen(false); setSaving(false); toast(editId ? 'Student updated' : 'Student added');
  };
  const delStu = (id) => {
    if (!window.confirm('Delete student? This will also remove their payment records, attendance, marks and certificates.')) return;
    // Clean attendance — remove student key from every date
    const newAtt = Object.fromEntries(
      Object.entries(db.att || {}).map(([dt, rec]) => {
        const { [id]: _, ...rest } = rec;
        return [dt, rest];
      })
    );
    // Clean marks — remove student key from every exam entry
    const newMarks = Object.fromEntries(
      Object.entries(db.marks || {}).map(([key, rec]) => {
        const { [id]: _, ...rest } = rec;
        return [key, rest];
      })
    );
    save({
      ...db,
      students: db.students.filter(s => (s.sid || s.id) !== id),
      photos: Object.fromEntries(Object.entries(db.photos).filter(([k]) => k !== id)),
      pays: (db.pays || []).filter(p => p.sid !== id),
      att: newAtt,
      marks: newMarks,
      certRegistry: (db.certRegistry || []).filter(r => r.stuId !== id),
    });
    toast('Deleted', 'err');
  };

  const filtered = db.students.filter(s => {
    const txt = (s.fn + ' ' + s.ln + ' ' + s.cls + ' ' + (s.father || '') + ' ' + (s.fphone || '') + ' ' + (s.admno || '')).toLowerCase();
    const matchSearch = !search || txt.includes(search.toLowerCase());
    const matchCls = !filterCls || s.cls === filterCls;
    const matchStatus = !filterStatus || s.fst === filterStatus;
    return matchSearch && matchCls && matchStatus;
  });

  // Real-time outstanding from actual payments
  const outstanding = db.students.reduce((sum, s) => {
    const cf = (db.settings?.classFees || {})[s.cls] || {};
    const monthly = parseFloat(cf.monthly) || 0;
    const annualFee = monthly > 0 ? monthly * 12 + (parseFloat(cf.exam) || 0) : 0;
    const paid = tuitionPaid(db.pays, s.id);
    return sum + Math.max(0, annualFee - paid);
  }, 0);

  const feeStatusCls = (fst) => {
    if (fst === 'Paid') return 'bg-secondary-container text-on-secondary-container';
    if (fst === 'Overdue') return 'bg-error-container text-on-error-container';
    return 'bg-tertiary-fixed text-on-tertiary-fixed-variant';
  };

  return (
    <div>
      {/* Hero Header */}
      <div className="directory-hero mb-6 md:mb-8 flex flex-col md:flex-row md:justify-between md:items-end gap-3 md:gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-primary tracking-tight mb-2 font-headline">Student Directory</h1>
          <p className="text-on-surface-variant font-medium">
            {db.students.length} active enrollment{db.students.length !== 1 ? 's' : ''} · {db.settings.year || '2025-26'}
          </p>
        </div>
        <div className="directory-hero-actions flex flex-wrap gap-2 md:gap-3 w-full md:w-auto">
          <button onClick={openAdd} className="px-4 md:px-6 py-2.5 bg-primary text-on-primary rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-primary/20 hover:scale-[1.02] transition-transform border-0 cursor-pointer font-sans flex-1 md:flex-none">
            <span className="material-symbols-outlined text-sm">add</span> Enroll New Student
          </button>
          <button onClick={openImport} className="px-4 md:px-5 py-2.5 bg-surface-container-highest text-primary rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-surface-container-high transition-colors border-0 cursor-pointer font-sans flex-1 md:flex-none">
            <span className="material-symbols-outlined text-sm">upload</span> Import from Session
          </button>
          <button onClick={refreshFromServer} disabled={refreshing} title="Reload from server" className="px-4 md:px-5 py-2.5 bg-surface-container-highest text-primary rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-surface-container-high transition-colors border-0 cursor-pointer font-sans flex-1 md:flex-none" style={{ opacity: refreshing ? 0.6 : 1 }}>
            <span className="material-symbols-outlined text-sm" style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>sync</span> {refreshing ? 'Syncing…' : 'Sync'}
          </button>
          <button onClick={() => setPage && setPage('rep')} className="directory-desktop-only px-4 md:px-5 py-2.5 bg-surface-container-highest text-primary rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-surface-container-high transition-colors border-0 cursor-pointer font-sans flex-1 md:flex-none">
            <span className="material-symbols-outlined text-sm">download</span> Export Data
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[{ id: 'summary', icon: 'table_rows', label: 'Class Summary' }, { id: 'directory', icon: 'person_search', label: 'Directory' }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 150ms',
              background: view === v.id ? '#1960a3' : '#f1f5f9',
              color: view === v.id ? '#fff' : '#64748b'
            }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{v.icon}</span>
            {v.label}
          </button>
        ))}
      </div>

      {/* Class Summary View */}
      {view === 'summary' && (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,32,69,0.05)', marginBottom: 28 }}>
          {/* Mobile: card list */}
          <div style={{ display: 'none' }} className="summary-mobile">
            {db.classes.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No classes added yet</div>
            ) : db.classes.map((cls, i) => {
              const students = db.students.filter(s => s.cls === cls.name);
              const boys = students.filter(s => (s.gn || 'Male') === 'Male').length;
              const girls = students.filter(s => s.gn === 'Female').length;
              return (
                <div key={cls.id} style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>{cls.name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      <span style={{ color: '#1960a3', fontWeight: 700 }}>{boys}B</span>
                      {' · '}
                      <span style={{ color: '#7c3aed', fontWeight: 700 }}>{girls}G</span>
                      {' · '}
                      <span style={{ fontWeight: 800, color: '#0f172a' }}>{students.length} total</span>
                    </div>
                  </div>
                  <button onClick={() => { setFilterCls(cls.name); setView('directory'); }}
                    style={{ fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#eef4ff', color: '#1960a3', cursor: 'pointer' }}>
                    View
                  </button>
                </div>
              );
            })}
            {db.classes.length > 0 && (
              <div style={{ padding: '12px 16px', background: '#1960a3', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 800, color: '#fff', fontSize: 13 }}>TOTAL</span>
                <span style={{ fontWeight: 800, color: '#fff' }}>{db.students.length} students</span>
              </div>
            )}
          </div>
          {/* Desktop: table */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }} className="summary-table">
            <thead>
              <tr style={{ background: '#1960a3' }}>
                {['Class/Grade', 'Boys', 'Girls', 'Total Students', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '14px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {db.classes.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No classes added yet</td></tr>
              ) : db.classes.map((cls, i) => {
                const students = db.students.filter(s => s.cls === cls.name);
                const boys = students.filter(s => (s.gn || 'Male') === 'Male').length;
                const girls = students.filter(s => s.gn === 'Female').length;
                const isExpanded = expandedCls === cls.id;
                return (
                  <React.Fragment key={cls.id}>
                    <tr style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc', cursor: 'pointer' }}
                      onClick={() => setExpandedCls(isExpanded ? null : cls.id)}>
                      <td style={{ padding: '14px 20px', fontWeight: 600, color: '#1e293b', fontSize: 14 }}>{cls.name}</td>
                      <td style={{ padding: '14px 20px', color: '#1960a3', fontWeight: 700 }}>{boys}</td>
                      <td style={{ padding: '14px 20px', color: '#7c3aed', fontWeight: 700 }}>{girls}</td>
                      <td style={{ padding: '14px 20px', fontWeight: 800, color: '#0f172a', fontSize: 15 }}>{students.length}</td>
                      <td style={{ padding: '14px 20px' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button onClick={e => { e.stopPropagation(); setFilterCls(cls.name); setView('directory'); }}
                            style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 8, border: 'none', background: '#eef4ff', color: '#1960a3', cursor: 'pointer' }}>
                            View Students
                          </button>
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#94a3b8', transition: 'transform 200ms', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>expand_more</span>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && students.length > 0 && (
                      <tr key={cls.id + '_exp'}>
                        <td colSpan={5} style={{ padding: '0 20px 16px', background: '#f8fafc' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 12 }}>
                            {students.map(s => (
                              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '6px 12px', fontSize: 12 }}>
                                <span style={{ fontWeight: 700, color: '#1e293b' }}>{s.fn} {s.ln}</span>
                                <span style={{ color: '#94a3b8' }}>Roll {s.roll || '—'}</span>
                                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: s.gn === 'Female' ? '#ede9fe' : '#dbeafe', color: s.gn === 'Female' ? '#7c3aed' : '#1960a3', fontWeight: 700 }}>{s.gn || 'Male'}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {db.classes.length > 0 && (
                <tr style={{ background: '#1960a3', borderTop: '2px solid #1960a3' }}>
                  <td style={{ padding: '12px 20px', fontWeight: 800, color: '#fff', fontSize: 13 }}>TOTAL</td>
                  <td style={{ padding: '12px 20px', fontWeight: 800, color: '#fff' }}>{db.students.filter(s => (s.gn || 'Male') === 'Male').length}</td>
                  <td style={{ padding: '12px 20px', fontWeight: 800, color: '#fff' }}>{db.students.filter(s => s.gn === 'Female').length}</td>
                  <td style={{ padding: '12px 20px', fontWeight: 800, color: '#fff', fontSize: 15 }}>{db.students.length}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
          <style>{`
            @media (max-width: 640px) {
              .summary-table { display: none !important; }
              .summary-mobile { display: block !important; }
            }
          `}</style>
        </div>
      )}

      {/* Directory View */}
      {view === 'directory' && <>

        {/* Filter Bar + Outstanding Bento */}
        <div className="directory-filter-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, marginBottom: 28 }}>
          {/* Search + filters */}
          <div className="directory-filter-panel" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', boxShadow: '0 2px 8px rgba(0,32,69,0.05)' }}>
            {/* Search with live dropdown */}
            <div className="directory-search-group" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Search</label>
              <div ref={searchRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span className="material-symbols-outlined" style={{ position: 'absolute', left: 10, fontSize: 16, color: '#94a3b8', pointerEvents: 'none', zIndex: 1 }}>search</span>
                <input
                  style={{ paddingLeft: 34, paddingRight: search ? 32 : 12, paddingTop: 8, paddingBottom: 8, borderRadius: 24, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#1e293b', outline: 'none', width: 'min(100%, 260px)', transition: 'all 200ms', fontFamily: 'inherit' }}
                  value={search}
                  onChange={e => { setSearch(e.target.value); setSearchOpen(true); }}
                  onFocus={e => { e.target.style.borderColor = '#1960a3'; e.target.style.background = '#f0f6ff'; setSearchOpen(true); }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                  onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); setSearchOpen(false); } }}
                  placeholder="Name, roll, phone, class…"
                />
                {search && (
                  <button onClick={() => { setSearch(''); setSearchOpen(false); }} style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: '#94a3b8', zIndex: 1 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
                  </button>
                )}
                {/* Live dropdown */}
                {searchOpen && search && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 'min(92vw, 320px)', background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,32,69,0.14)', border: '1px solid #e8edf5', zIndex: 999, overflow: 'hidden' }}>
                    {suggestions.length > 0 ? suggestions.map((s, i) => {
                      const paid = paidTotal(db.pays, s.id);
                      const bal = Math.max(0, (s.fee || 0) - paid);
                      const photo = db.photos[s.id];
                      return (
                        <button key={s.id} onMouseDown={() => { setSearch(`${s.fn} ${s.ln}`); setSearchOpen(false); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'background 120ms', borderBottom: '1px solid #f8fafc' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0f6ff'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          {/* avatar */}
                          <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, color: '#fff' }}>
                            {photo ? <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : `${(s.fn || '')[0] || ''}${(s.ln || '')[0] || ''}`.toUpperCase()}
                          </div>
                          {/* info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.fn} {s.ln}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.cls || '—'} {s.roll ? `· Roll ${s.roll}` : ''} {s.admno ? `· ${s.admno}` : ''}</div>
                          </div>
                          {/* fee badge */}
                          <span style={{
                            fontSize: 10, fontWeight: 700, flexShrink: 0, padding: '3px 8px', borderRadius: 20,
                            background: bal === 0 ? '#d1fae5' : s.fst === 'Overdue' ? '#fee2e2' : '#fef3c7',
                            color: bal === 0 ? '#059669' : s.fst === 'Overdue' ? '#ef4444' : '#d97706'
                          }}>
                            {bal === 0 ? 'Paid' : '₹' + bal.toLocaleString('en-IN')}
                          </span>
                        </button>
                      );
                    }) : (
                      <div style={{ padding: '20px 16px', textAlign: 'center' }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#eef4ff,#dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 22, color: '#1960a3', fontVariationSettings: "'FILL' 0,'wght' 300" }}>person_search</span>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>No students match</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>"{search}"</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Class filter */}
            <div className="directory-class-group" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Class</label>
              <select value={filterCls} onChange={e => setFilterCls(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#1e293b', outline: 'none', minWidth: 130, cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}>
                <option value="">All Classes</option>
                {classes.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            {/* Fee status filter */}
            <div className="directory-status-group directory-desktop-only" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Fee Status</label>
              <div className="directory-status-pills" style={{ display: 'flex', gap: 6 }}>
                {['', 'Paid', 'Pending', 'Overdue'].map(s => (
                  <button key={s} onClick={() => setFilterStatus(s)}
                    style={{
                      padding: '7px 14px', borderRadius: 20, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 150ms',
                      background: filterStatus === s ? '#1960a3' : '#f1f5f9',
                      color: filterStatus === s ? '#fff' : '#64748b',
                      boxShadow: filterStatus === s ? '0 2px 8px rgba(25,96,163,0.25)' : 'none',
                    }}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
            </div>
            {/* Result count */}
            {(search || filterCls || filterStatus) && (
              <div className="directory-clear-wrap" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
                <button onClick={() => { setSearch(''); setFilterCls(''); setFilterStatus(''); }}
                  style={{ fontSize: 11, color: '#ef4444', background: '#fee2e2', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
                  Clear
                </button>
              </div>
            )}
          </div>
          {/* Outstanding card */}
          <div className="directory-outstanding-card directory-desktop-only" style={{ background: 'linear-gradient(135deg,#1960a3,#002045)', borderRadius: 16, padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 180, boxShadow: '0 4px 16px rgba(25,96,163,0.25)' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Outstanding</p>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: '#fff', fontFamily: 'Manrope,sans-serif', margin: 0 }}>₹{outstanding.toLocaleString('en-IN')}</h3>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>{db.students.filter(s => paidTotal(db.pays, s.id) < (s.fee || 0)).length} pending</p>
          </div>
        </div>

        {/* Table */}
        {db.students.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 24, padding: '72px 24px', textAlign: 'center', boxShadow: '0 1px 8px rgba(0,31,77,0.06)' }}>
            <div style={{ width: 96, height: 96, borderRadius: 28, background: 'linear-gradient(135deg,#eef4ff 0%,#dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 4px 24px rgba(25,96,163,0.13)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#1960a3', fontVariationSettings: "'FILL' 0,'wght' 300" }}>group</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', marginBottom: 8, fontFamily: 'Manrope,sans-serif' }}>No students enrolled yet</div>
            <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 24 }}>Add your first student to get started</div>
            <button onClick={openAdd} style={{ padding: '11px 28px', background: 'linear-gradient(135deg,#1960a3,#6366f1)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(25,96,163,0.3)', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person_add</span>
              Enroll First Student
            </button>
          </div>
        ) : (
          <div className="bg-surface-container-lowest rounded-2xl overflow-hidden shadow-sm">
            <TblWrap className="student-directory-desktop-table">
              <table>
                <thead>
                  <tr className="bg-surface-container-low/50">
                    <th className="px-6 py-4">Roll No</th>
                    <th className="px-6 py-4">Student Name</th>
                    <th className="px-6 py-4">Class</th>
                    <th className="px-6 py-4">Parent's Name</th>
                    <th className="px-6 py-4">Contact</th>
                    <th className="px-6 py-4">Fee Status</th>
                    <th className="px-6 py-4">Modified By</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => {
                    const photo = db.photos[s.id];
                    const paid = paidTotal(db.pays, s.id);
                    const fst = paid >= s.fee && s.fee > 0 ? 'Paid' : s.fst;
                    return (
                      <tr key={s.id} className="group hover:bg-surface-container-low/30 transition-colors">
                        <td className="px-6 py-5">
                          <span className="font-bold text-primary">#{s.roll || s.admno || (i + 1)}</span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden bg-surface-container-low">
                              {photo
                                ? <img src={photo} alt={s.fn} className="w-full h-full object-cover" />
                                : <Avatar name={s.fn + ' ' + s.ln} idx={i} />
                              }
                            </div>
                            <div>
                              <p className="font-bold text-on-surface text-sm">{s.fn} {s.ln}</p>
                              <p className="text-[10px] text-on-surface-variant uppercase font-medium">{s.admno ? 'ADM: ' + s.admno : s.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="px-3 py-1 bg-surface-container text-on-surface text-xs font-bold rounded-md">{s.cls || '—'}</span>
                        </td>
                        <td className="px-6 py-5">
                          <p className="text-sm font-medium text-on-surface">{s.father || '—'}</p>
                          {s.mother && <p className="text-[10px] text-on-surface-variant">{s.mother}</p>}
                        </td>
                        <td className="px-6 py-5">
                          <p className="text-sm text-on-surface">{s.fphone || s.ph || '—'}</p>
                        </td>
                        <td className="px-6 py-5">
                          <span className={`px-3 py-1.5 text-[10px] font-extrabold uppercase rounded-full tracking-wider ${feeStatusCls(fst)}`}>
                            {fst} {s.fee > 0 ? '(₹' + paid.toLocaleString('en-IN') + '/' + s.fee.toLocaleString('en-IN') + ')' : ''}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          {s._lastModifiedBy ? (
                            <div>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: '#fef3c7', border: '1px solid #fde68a' }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 12, color: '#d97706' }}>edit_note</span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>{s._lastModifiedBy}</span>
                              </span>
                              {s._lastModifiedAt && (
                                <p style={{ fontSize: 9, color: '#94a3b8', marginTop: 3 }}>
                                  {s._lastModifiedType === 'add' ? 'Added' : 'Edited'} · {new Date(s._lastModifiedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                          )}
                        </td>
                        <td className="px-6 py-5 text-right">
                          <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEdit(s)} title="Edit"
                              className="p-2 hover:bg-primary-container hover:text-on-primary rounded-lg transition-all text-primary border-0 bg-transparent cursor-pointer">
                              <span className="material-symbols-outlined text-lg">edit</span>
                            </button>
                            <button onClick={() => setPage && setPage('docs')} title="Generate ID"
                              className="p-2 hover:bg-primary-container hover:text-on-primary rounded-lg transition-all text-primary border-0 bg-transparent cursor-pointer">
                              <span className="material-symbols-outlined text-lg">badge</span>
                            </button>
                            <button onClick={() => delStu(s.id)} title="Delete"
                              className="p-2 hover:bg-error-container hover:text-error rounded-lg transition-all text-outline border-0 bg-transparent cursor-pointer">
                              <span className="material-symbols-outlined text-lg">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filtered.length && (
                    <tr><td colSpan={8} style={{ padding: '48px 24px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg,#eef4ff 0%,#dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(25,96,163,0.12)' }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 34, color: '#1960a3', fontVariationSettings: "'FILL' 0,'wght' 300" }}>person_search</span>
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>No students found</div>
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>Try adjusting your search or filters</div>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </TblWrap>
            <div className="student-directory-mobile-list">
              {filtered.map((s, i) => {
                const photo = db.photos[s.id];
                const paid = paidTotal(db.pays, s.id);
                const fst = paid >= s.fee && s.fee > 0 ? 'Paid' : s.fst;
                return (
                  <div key={s.id} className="student-directory-mobile-card">
                    <div className="student-directory-mobile-head">
                      <div className="student-directory-mobile-id">
                        <span>#{s.roll || s.admno || (i + 1)}</span>
                        <small>{s.admno ? `ADM: ${s.admno}` : s.id}</small>
                      </div>
                      <span className={`student-directory-mobile-fee ${feeStatusCls(fst)}`}>
                        {fst}
                      </span>
                    </div>

                    <div className="student-directory-mobile-student">
                      <div className="student-directory-mobile-avatar">
                        {photo
                          ? <img src={photo} alt={s.fn} className="w-full h-full object-cover" />
                          : <Avatar name={s.fn + ' ' + s.ln} idx={i} />
                        }
                      </div>
                      <div>
                        <div className="student-directory-mobile-name">{s.fn} {s.ln}</div>
                        <div className="student-directory-mobile-class">{s.cls || 'Class not assigned'}</div>
                      </div>
                    </div>

                    <div className="student-directory-mobile-meta">
                      <div className="student-directory-mobile-row">
                        <span className="material-symbols-outlined">family_restroom</span>
                        <span>{s.father || 'Parent name not added'}</span>
                      </div>
                      <div className="student-directory-mobile-row">
                        <span className="material-symbols-outlined">call</span>
                        <span>{s.fphone || s.ph || 'No contact added'}</span>
                      </div>
                      <div className="student-directory-mobile-row">
                        <span className="material-symbols-outlined">payments</span>
                        <span>{s.fee > 0 ? `₹${paid.toLocaleString('en-IN')} / ₹${s.fee.toLocaleString('en-IN')}` : 'Fee not set'}</span>
                      </div>
                    </div>

                    <div className="student-directory-mobile-actions">
                      <button onClick={() => openEdit(s)} className="btn">
                        <span className="material-symbols-outlined text-sm">edit</span>
                        Edit
                      </button>
                      <button onClick={() => setPage && setPage('docs')} className="btn">
                        <span className="material-symbols-outlined text-sm">badge</span>
                        ID Card
                      </button>
                      <button onClick={() => delStu(s.id)} className="btn btn-danger">
                        <span className="material-symbols-outlined text-sm">delete</span>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
              {!filtered.length && (
                <div className="student-directory-mobile-empty">
                  <div className="student-directory-mobile-empty-icon">
                    <span className="material-symbols-outlined">person_search</span>
                  </div>
                  <div className="student-directory-mobile-empty-title">No students found</div>
                  <div className="student-directory-mobile-empty-text">Try adjusting your search or filters</div>
                </div>
              )}
            </div>
            {/* Footer */}
            <div className="directory-desktop-footer px-6 py-4 bg-surface-container-low/20 flex items-center justify-between border-t border-surface-container-low">
              <p className="text-xs text-on-surface-variant font-medium">
                Showing <span className="text-primary font-bold">{filtered.length}</span> of {db.students.length} records
              </p>
              <button onClick={openAdd} className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline border-0 bg-transparent cursor-pointer">
                <span className="material-symbols-outlined text-sm">person_add</span> Add Student
              </button>
            </div>
          </div>
        )}
      </>}

      {/* Insight Footer */}
      <div className="directory-desktop-only mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5">
        {[
          { icon: 'trending_up', label: 'Total Enrolled', value: db.students.length + ' students' },
          { icon: 'payments', label: 'Fee Collected', value: '₹' + db.pays.reduce((s, p) => s + p.amt, 0).toLocaleString('en-IN') },
          { icon: 'how_to_reg', label: 'Fully Paid', value: db.students.filter(s => paidTotal(db.pays, s.id) >= s.fee && s.fee > 0).length + ' students' },
        ].map(({ icon, label, value }) => (
          <div key={label} className="bg-surface-container-low p-5 rounded-2xl relative overflow-hidden group">
            <div className="relative z-10">
              <p className="text-[10px] font-bold uppercase tracking-widest text-primary/60 mb-1">{label}</p>
              <h4 className="text-xl font-extrabold text-primary font-headline">{value}</h4>
            </div>
            <span className="material-symbols-outlined absolute -right-3 -bottom-3 text-8xl text-primary/5 group-hover:scale-110 transition-transform duration-500">{icon}</span>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={(editId ? 'Edit' : 'Add') + ' Student'} wide>
        <div className="mb-4">
          <div className="text-[10px] font-black text-primary uppercase tracking-widest mb-2 pb-2" style={{ borderBottom: '1px solid rgba(0,32,69,0.1)' }}>Student Photo (for ID Card)</div>
          <PhotoZone photo={photo} onUpload={setPhoto} onClear={() => setPhoto(null)} />
        </div>
        <Grid cols={2}>
          <SecLabel>Personal</SecLabel>
          <Field label="First Name *"><Input value={form.fn || ''} onChange={v => setForm(f => ({ ...f, fn: v }))} placeholder="Enter first name" /></Field>
          <Field label="Last Name"><Input value={form.ln || ''} onChange={v => setForm(f => ({ ...f, ln: v }))} placeholder="Enter last name" /></Field>
          <Field label="Date of Birth"><Input type="date" value={form.dob || ''} onChange={v => setForm(f => ({ ...f, dob: v }))} /></Field>
          <Field label="Gender"><Select value={form.gn || 'Male'} onChange={v => setForm(f => ({ ...f, gn: v }))}><option>Male</option><option>Female</option><option>Other</option></Select></Field>
          <Field label="Blood Group"><Select value={form.bl || ''} onChange={v => setForm(f => ({ ...f, bl: v, blood: v }))}><option value="">—</option>{['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(b => <option key={b}>{b}</option>)}</Select></Field>
          <Field label="Caste"><Input value={form.ca || ''} onChange={v => setForm(f => ({ ...f, ca: v, caste: v }))} placeholder="Enter caste" /></Field>
          <Field label="Aadhaar"><Input value={form.aa || ''} onChange={v => setForm(f => ({ ...f, aa: v }))} placeholder="12-digit Aadhaar number" /></Field>
          <Field label="Religion"><Input value={form.re || ''} onChange={v => setForm(f => ({ ...f, re: v, religion: v }))} placeholder="Enter religion" /></Field>
          <SecLabel>Academic</SecLabel>
          <Field label="Class *"><Select value={form.cls || ''} onChange={v => setForm(f => ({ ...f, cls: v }))}><option value="">—</option>{classes.map(c => <option key={c}>{c}</option>)}</Select></Field>
          <Field label="Roll No."><Input value={form.ro || ''} onChange={v => setForm(f => ({ ...f, ro: v }))} placeholder="Enter roll number" /></Field>
          <Field label="Admission No."><Input value={form.an || ''} onChange={v => setForm(f => ({ ...f, an: v, admno: v }))} placeholder="Enter admission number" /></Field>
          <Field label="PEN No."><Input value={form.pen || ''} onChange={v => setForm(f => ({ ...f, pen: v }))} placeholder="11-digit PEN number" /></Field>
          <Field label="APAAR ID"><Input value={form.apaar || ''} onChange={v => setForm(f => ({ ...f, apaar: v }))} placeholder="12-digit APAAR ID" /></Field>
          <Field label="Admission Date"><Input type="date" value={form.ad || ''} onChange={v => setForm(f => ({ ...f, ad: v }))} /></Field>
          <Field label="Previous School"><Input value={form.ps || ''} onChange={v => setForm(f => ({ ...f, ps: v }))} /></Field>
          <Field label="Email"><Input type="email" value={form.em || ''} onChange={v => setForm(f => ({ ...f, em: v }))} /></Field>
          <SecLabel>Father</SecLabel>
          <Field label="Father Name"><Input value={form.fa || ''} onChange={v => setForm(f => ({ ...f, fa: v, father: v }))} placeholder="Enter father's full name" /></Field>
          <Field label="Occupation"><Input value={form.fo || ''} onChange={v => setForm(f => ({ ...f, fo: v }))} /></Field>
          <Field label="Father Phone"><Input value={form.fp || ''} onChange={v => setForm(f => ({ ...f, fp: v, fphone: v }))} placeholder="10-digit mobile number" /></Field>
          <Field label="Father Aadhaar"><Input value={form.faa || ''} onChange={v => setForm(f => ({ ...f, faa: v }))} /></Field>
          <SecLabel>Mother</SecLabel>
          <Field label="Mother Name"><Input value={form.ma || ''} onChange={v => setForm(f => ({ ...f, ma: v, mother: v }))} placeholder="Enter mother's full name" /></Field>
          <Field label="Occupation"><Input value={form.mo || ''} onChange={v => setForm(f => ({ ...f, mo: v }))} /></Field>
          <Field label="Mother Phone"><Input value={form.mp || ''} onChange={v => setForm(f => ({ ...f, mp: v }))} /></Field>
          <Field label="Mother Aadhaar"><Input value={form.maa || ''} onChange={v => setForm(f => ({ ...f, maa: v }))} /></Field>
          <SecLabel>Address</SecLabel>
          <Field label="Phone"><Input value={form.ph || ''} onChange={v => setForm(f => ({ ...f, ph: v }))} /></Field>
          <Span><Field label="Address"><Input value={form.addr || ''} onChange={v => setForm(f => ({ ...f, addr: v }))} /></Field></Span>
          <Field label="City"><Input value={form.cy || ''} onChange={v => setForm(f => ({ ...f, cy: v, city: v }))} /></Field>
          <Field label="PIN"><Input value={form.pin || ''} onChange={v => setForm(f => ({ ...f, pin: v }))} /></Field>
          <SecLabel>Medical</SecLabel>
          <Field label="Condition"><Input value={form.mc || ''} onChange={v => setForm(f => ({ ...f, mc: v }))} placeholder="Any known condition or None" /></Field>
          <Field label="Allergies"><Input value={form.al || ''} onChange={v => setForm(f => ({ ...f, al: v }))} placeholder="Any allergies or None" /></Field>
          <SecLabel>Parent Portal Access</SecLabel>
          {/* Hidden dummy inputs prevent browser autofill on the real fields */}
          <input type="text" name="prevent_autofill_user" style={{ display: 'none' }} readOnly autoComplete="off" />
          <input type="password" name="prevent_autofill_pass" style={{ display: 'none' }} readOnly autoComplete="off" />
          <Field label="Username"><Input value={form.puser || ''} onChange={v => setForm(f => ({ ...f, puser: v }))} placeholder="Leave blank for no access" autoComplete="off" /></Field>
          <Field label="Password">
            <div className="relative">
              <Input type={showPpass ? "text" : "password"} value={form.ppass || ''} onChange={v => setForm(f => ({ ...f, ppass: v }))} placeholder="Minimum 6 characters" autoComplete="new-password" style={{ paddingRight: 40 }} />
              <button type="button" onClick={() => setShowPpass(!showPpass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-primary transition-colors border-0 bg-transparent cursor-pointer flex items-center justify-center p-1">
                <span className="material-symbols-outlined text-xl">{showPpass ? 'visibility_off' : 'visibility'}</span>
              </button>
            </div>
            <div className="text-xs text-on-surface-variant mt-1 ml-1 opacity-80">
              Leave empty to keep current password
            </div>
          </Field>
        </Grid>
        {editId && <AdminStudentDocs activeSessionId={activeSessionId} sid={db.students.find(s => s.id === editId)?.sid || editId} />}
        <ModalFooter><Btn onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="primary" onClick={saveStu} disabled={saving}>{saving ? 'Saving…' : 'Save Student'}</Btn></ModalFooter>
      </Modal>

      {/* ── Import from Previous Session Modal ── */}
      {importOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ width: '100%', maxWidth: 640, background: '#fff', borderRadius: 20, boxShadow: '0 24px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#fff', fontVariationSettings: "'FILL' 1" }}>upload</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>Import Students from Previous Session</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  {importStep === 'pick' ? 'Select a session to import from' : `${filteredImport.length} students · ${selectedCount} selected`}
                </div>
              </div>
              <button onClick={() => setImportOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, display: 'flex', alignItems: 'center' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 22 }}>close</span>
              </button>
            </div>

            {/* Step 1: Pick session */}
            {importStep === 'pick' && (
              <div style={{ padding: 24, overflowY: 'auto' }}>
                {importLoading && <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>Loading…</div>}
                {!importLoading && importSessions.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 36, display: 'block', marginBottom: 8 }}>history</span>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>No previous sessions found</div>
                  </div>
                )}
                {!importLoading && importSessions.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {importSessions.map(sess => (
                      <button key={sess.id} onClick={() => loadImportStudents(sess.sid || sess.id)} disabled={importLoading}
                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', textAlign: 'left', transition: 'all 150ms' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#1960a3'; e.currentTarget.style.background = '#eef4ff'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#f8fafc'; }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#fff', fontVariationSettings: "'FILL' 1" }}>school</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{sess.name}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{sess.year}</div>
                        </div>
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#94a3b8' }}>chevron_right</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Assign classes */}
            {importStep === 'assign' && (
              <>
                <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                  <input value={importSearch} onChange={e => setImportSearch(e.target.value)} placeholder="Search students…"
                    style={{ flex: 1, minWidth: 140, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, outline: 'none' }} />
                  <select value={importFilterCls} onChange={e => setImportFilterCls(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#1e293b', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <option value="">All Classes</option>
                    {importClasses.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={() => { const s = { ...importSelected }; filteredImport.forEach(st => { s[st.sid || st.id] = st.cls || ''; }); setImportSelected(s); }}
                    style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#1960a3', cursor: 'pointer' }}>
                    Select All
                  </button>
                  <button onClick={() => { const s = { ...importSelected }; filteredImport.forEach(st => { s[st.sid || st.id] = '__skip__'; }); setImportSelected(s); }}
                    style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>
                    Deselect All
                  </button>
                  <button onClick={() => setImportStep('pick')}
                    style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11, fontWeight: 600, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_back</span> Back
                  </button>
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {filteredImport.length === 0
                    ? <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: 13 }}>No students found</div>
                    : filteredImport.map(s => {
                      const sid = s.sid || s.id;
                      const isSkipped = importSelected[sid] === '__skip__';
                      const alreadyExists = db.students.some(x => (x.sid || x.id) === sid);
                      return (
                        <div key={sid} style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px', borderBottom: '1px solid #f8fafc',
                          background: alreadyExists ? '#f8fafc' : isSkipped ? '#fff' : '#f0fdf4', opacity: alreadyExists ? 0.5 : 1
                        }}>
                          <input type="checkbox" checked={!isSkipped && !alreadyExists} disabled={alreadyExists}
                            onChange={e => setImportSelected(prev => ({ ...prev, [sid]: e.target.checked ? (s.cls || '') : '__skip__' }))}
                            style={{ width: 16, height: 16, cursor: alreadyExists ? 'not-allowed' : 'pointer', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{s.fn} {s.ln}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              {alreadyExists
                                ? <span style={{ color: '#059669', fontWeight: 600 }}>✓ Already in this session</span>
                                : <>Prev class: <b>{s.cls || '—'}</b>{s.admno ? ` · Adm: ${s.admno}` : ''}</>}
                            </div>
                          </div>
                          {!alreadyExists && !isSkipped && (
                            <select value={importSelected[sid] || ''} onChange={e => setImportSelected(prev => ({ ...prev, [sid]: e.target.value }))}
                              style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #c7d9f5', fontSize: 12, color: '#1e293b', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                              <option value="">— No class —</option>
                              {classes.map(c => <option key={c} value={c}>{c}{c === s.cls ? ' (same)' : ''}</option>)}
                            </select>
                          )}
                        </div>
                      );
                    })
                  }
                </div>
                <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, background: '#fff' }}>
                  <button onClick={() => setImportOpen(false)}
                    style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={doImport} disabled={importSaving || selectedCount === 0}
                    style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1960a3,#6366f1)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: selectedCount === 0 ? 0.5 : 1 }}>
                    {importSaving ? 'Importing…' : `Import ${selectedCount} Student${selectedCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Subject pill colour cycling

export default Students;
